import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { motion } from 'framer-motion';
import { ArrowLeft, Send, Loader2, Bot, User } from 'lucide-react';

const subjectIcons = { Math: '📐', Science: '🧬', English: '📖', 'Social Science': '🌍', Hindi: '🔤', Physics: '⚛️', Chemistry: '🧪', Biology: '🧬' };

export default function ChapterChat({ params, onNavigate }) {
  const { api, user } = useAuth();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [chapterName, setChapterName] = useState('');
  const chatEndRef = useRef(null);

  const subject = params?.subject || 'Math';
  const chapterNum = params?.chapter || 1;

  // Fetch chapter details
  const fetchChapterName = useCallback(async () => {
    try {
      const res = await api.get(`/chapters?class_name=${encodeURIComponent(user?.class_name || 'Class 8')}&subject=${encodeURIComponent(subject)}`);
      const chapter = res.data?.find(c => c.number === parseInt(chapterNum));
      if (chapter) {
        setChapterName(chapter.name);
      }
    } catch (err) { console.error(err); }
  }, [api, user, subject, chapterNum]);

  const fetchHistory = useCallback(async () => {
    try {
      const res = await api.get(`/chat/${subject}/${chapterNum}`);
      setMessages(res.data);
      if (res.data.length === 0) {
        const welcomeText = chapterName 
          ? `Hi! I'm your Claw Coach for ${subject} Ch ${chapterNum}: ${chapterName}. Ask me anything about this chapter - I'll explain step by step!`
          : `Hi! I'm your Claw Coach for ${subject} Chapter ${chapterNum}. Ask me anything about this chapter - I'll explain step by step!`;
        setMessages([{
          id: 'welcome',
          role: 'assistant',
          text: welcomeText,
          created_at: new Date().toISOString()
        }]);
      }
    } catch (err) { console.error(err); }
  }, [api, subject, chapterNum, chapterName]);

  useEffect(() => { fetchChapterName(); }, [fetchChapterName]);
  useEffect(() => { fetchHistory(); }, [fetchHistory]);
  useEffect(() => { chatEndRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages]);

  const sendMessage = async () => {
    if (!input.trim() || sending) return;
    const text = input.trim();
    setInput('');
    setSending(true);

    // Optimistic add
    const tempMsg = { id: 'temp-user', role: 'user', text, created_at: new Date().toISOString() };
    setMessages(prev => [...prev, tempMsg]);

    try {
      const res = await api.post(`/chat/${subject}/${chapterNum}`, { message: text });
      setChapterName(res.data.chapter_name);
      setMessages(prev => [
        ...prev.filter(m => m.id !== 'temp-user'),
        { id: Date.now() + '-u', role: 'user', text, created_at: new Date().toISOString() },
        { id: Date.now() + '-a', role: 'assistant', text: res.data.response, created_at: new Date().toISOString() }
      ]);
    } catch (err) {
      console.error(err);
      setMessages(prev => prev.filter(m => m.id !== 'temp-user'));
    }
    setSending(false);
  };

  return (
    <div className="flex flex-col h-screen bg-[#0f172a]">
      {/* Header */}
      <div className="glass border-b border-slate-800/50 px-4 py-3 flex items-center gap-3 shrink-0">
        <button data-testid="back-from-chat" onClick={() => onNavigate('chapters', { subject })} className="text-slate-400 hover:text-white">
          <ArrowLeft className="w-5 h-5" />
        </button>
        <div className="w-9 h-9 rounded-xl bg-violet-600/20 flex items-center justify-center">
          <Bot className="w-5 h-5 text-violet-400" />
        </div>
        <div>
          <h1 className="text-sm font-bold text-white font-['Outfit']" data-testid="chat-title">
            {subjectIcons[subject]} Ch {chapterNum}: {chapterName || subject}
          </h1>
          <p className="text-[10px] text-slate-500">Claw AI Tutor - {user?.class_name}</p>
        </div>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto px-4 py-4 space-y-3">
        {messages.map((msg, i) => (
          <motion.div
            key={msg.id || i}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
          >
            <div className={`max-w-[85%] ${
              msg.role === 'user'
                ? 'bg-violet-600 text-white rounded-2xl rounded-br-md'
                : 'bg-slate-800/70 text-slate-200 rounded-2xl rounded-bl-md'
            } px-4 py-3`} data-testid={`chat-msg-${i}`}>
              {msg.role === 'assistant' && (
                <div className="flex items-center gap-1.5 mb-1">
                  <Bot className="w-3 h-3 text-violet-400" />
                  <span className="text-[10px] text-violet-400 font-medium">Claw Coach</span>
                </div>
              )}
              <p className="text-sm whitespace-pre-wrap leading-relaxed">{msg.text}</p>
            </div>
          </motion.div>
        ))}
        {sending && (
          <div className="flex justify-start">
            <div className="bg-slate-800/70 rounded-2xl rounded-bl-md px-4 py-3">
              <div className="flex items-center gap-2">
                <Loader2 className="w-4 h-4 text-violet-400 animate-spin" />
                <span className="text-xs text-slate-400">Thinking...</span>
              </div>
            </div>
          </div>
        )}
        <div ref={chatEndRef} />
      </div>

      {/* Input */}
      <div className="glass border-t border-slate-800/50 px-4 py-3 shrink-0">
        <div className="flex gap-2 max-w-lg mx-auto">
          <input
            data-testid="chat-input"
            type="text"
            value={input}
            onChange={e => setInput(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && sendMessage()}
            placeholder="Ask about this chapter..."
            className="flex-1 bg-slate-950/50 border border-slate-800 focus:border-violet-500 rounded-xl h-11 px-4 text-sm text-slate-100 placeholder:text-slate-600 outline-none"
          />
          <button
            data-testid="chat-send-btn"
            onClick={sendMessage}
            disabled={!input.trim() || sending}
            className="bg-violet-600 hover:bg-violet-700 text-white rounded-xl w-11 h-11 flex items-center justify-center transition-all disabled:opacity-40"
          >
            <Send className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
}
